import { useSwipeable } from 'react-swipeable';
import { cn } from '@repo/ui/lib/utils';
import { FC, useRef, useState, MouseEvent } from 'react';

import { type CandidateMedia, spotlightMediaTypes } from '@repo/ui/hooks';
import { Image } from './image/component';
import { Video } from './video/component';
import {
  NavigationControls,
  NavigationControlsPosition,
} from './navigation-controls/component';

export type SpotlightMediaReadyState = {
  disposeVideo: () => void;
  pauseVideo: () => void;
  startImageMediaAnimation: () => void;
};

export const SpotlightMedia: FC<{
  enableDesktopZoom?: boolean;
  className?: string;
  media: CandidateMedia;
  nextMedia?: CandidateMedia;
  videoAutoplay: boolean;
  isVideoMuted: boolean;
  imageMediaAlt?: string;
  videoClassName?: string;
  navigationControlsPosition?: NavigationControlsPosition;
  showPlayPauseVideoControl?: boolean;
  showVolumeVideoControl?: boolean;
  showNavigationControls?: boolean;
  showNavigationControlsOnHover?: boolean;
  showExpandNavigationControl?: boolean;
  // handlers
  onImageAnimationEnd?: () => void;
  onExpandClick?: () => void;
  onArrowLeftClick?: () => void;
  onArrowRightClick?: () => void;
  onMediaSwipeLeft?: () => void;
  onMediaSwipeRight?: () => void;
  onMediaClick?: (media: CandidateMedia) => void;
  onMediaDoubleClick?: () => void;
  onVolumeButtonClick?: (evt?: MouseEvent) => void;
  onMediaReady?: (data: SpotlightMediaReadyState) => void;
}> = ({
  onImageAnimationEnd,
  onExpandClick,
  onArrowLeftClick,
  onArrowRightClick,
  onMediaSwipeLeft,
  onMediaSwipeRight,
  onMediaClick,
  onMediaDoubleClick,
  onVolumeButtonClick,
  onMediaReady,
  enableDesktopZoom = false,
  className,
  media,
  nextMedia,
  videoAutoplay,
  isVideoMuted,
  imageMediaAlt,
  videoClassName,
  navigationControlsPosition,
  showPlayPauseVideoControl,
  showVolumeVideoControl = true,
  showNavigationControls = true,
  showExpandNavigationControl = true,
  showNavigationControlsOnHover,
}) => {
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onMediaSwipeLeft?.(),
    onSwipedRight: () => onMediaSwipeRight?.(),
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <span
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      <section
        {...swipeHandlers}
        className={cn('relative w-full h-full group', className)}
        data-testid="spotlight-media"
        onClick={() => onMediaClick?.(media)}
        onDoubleClick={() => {
          if (
            videoRef.current &&
            media.type === spotlightMediaTypes.video &&
            !videoRef.current.paused
          ) {
            videoRef.current.pause();
          }

          if (onMediaDoubleClick) {
            onMediaDoubleClick();
          }
        }}
      >
        {media.type === spotlightMediaTypes.image && (
          <Image
            media={media}
            nextMedia={nextMedia}
            enableDesktopZoom={enableDesktopZoom}
            imageMediaAlt={imageMediaAlt || ''}
            onImageAnimationEnd={onImageAnimationEnd!}
            onReady={(imageReadyState) =>
              onMediaReady?.({
                ...imageReadyState,
                disposeVideo: () => {},
                pauseVideo: () => {},
              })
            }
          />
        )}
        {media.type === spotlightMediaTypes.video && (
          <Video
            isHovered={isHovered}
            videoRef={videoRef}
            media={media}
            className={videoClassName}
            isMuted={isVideoMuted}
            autoplay={videoAutoplay}
            showPlayPauseVideoControl={showPlayPauseVideoControl}
            showVolumeControl={showVolumeVideoControl}
            onVolumeButtonClick={onVolumeButtonClick}
            onReady={(videoReadyState) =>
              onMediaReady?.({
                ...videoReadyState,
                startImageMediaAnimation: () => {},
              })
            }
          />
        )}
      </section>
      {showNavigationControls && (
        <NavigationControls
          isLoading={media.isProcessing}
          onExpandClick={onExpandClick!}
          onArrowLeftClick={onArrowLeftClick!}
          position={navigationControlsPosition}
          onArrowRightClick={onArrowRightClick!}
          showExpandNavigationControl={showExpandNavigationControl}
          showNavigationControlsOnHover={showNavigationControlsOnHover}
        />
      )}
    </span>
  );
};
